import { __extends } from "tslib";
import { Texture } from "../../../Materials/Textures/texture";
import { DynamicTexture } from "../../../Materials/Textures/dynamicTexture";
import { PostProcess } from "../../../PostProcesses/postProcess";
import { PostProcessRenderPipeline } from "../../../PostProcesses/RenderPipeline/postProcessRenderPipeline";
import { PostProcessRenderEffect } from "../../../PostProcesses/RenderPipeline/postProcessRenderEffect";
import "../../../PostProcesses/RenderPipeline/postProcessRenderPipelineManagerSceneComponent";
import "../../../Shaders/chromaticAberration.fragment";
import "../../../Shaders/lensHighlights.fragment";
import "../../../Shaders/depthOfField.fragment";
/**
 * BABYLON.JS Chromatic Aberration GLSL Shader
 * Author: Olivier Guyot
 * Separates very slightly R, G and B colors on the edges of the screen
 * Inspired by Francois Tarlier & Martins Upitis
 */
var LensRenderingPipeline = /** @class */ (function (_super) {
    __extends(LensRenderingPipeline, _super);
    /**
     * @constructor
     *
     * Effect parameters are as follow:
     * {
     *      chromatic_aberration: number;       // from 0 to x (1 for realism)
     *      edge_blur: number;                  // from 0 to x (1 for realism)
     *      distortion: number;                 // from 0 to x (1 for realism)
     *      grain_amount: number;               // from 0 to 1
     *      grain_texture: BABYLON.Texture;     // texture to use for grain effect; if unset, use random B&W noise
     *      dof_focus_distance: number;         // depth-of-field: focus distance; unset to disable (disabled by default)
     *      dof_aperture: number;               // depth-of-field: focus blur bias (default: 1)
     *      dof_darken: number;                 // depth-of-field: darken that which is out of focus (from 0 to 1, disabled by default)
     *      dof_pentagon: boolean;              // depth-of-field: makes a pentagon-like "bokeh" effect
     *      dof_gain: number;                   // depth-of-field: highlights gain; unset to disable (disabled by default)
     *      dof_threshold: number;              // depth-of-field: highlights threshold (default: 1)
     *      blur_noise: boolean;                // add a little bit of noise to the blur (default: true)
     * }
     * Note: if an effect parameter is unset, effect is disabled
     *
     * @param name The rendering pipeline name
     * @param parameters - An object containing all parameters (see above)
     * @param scene The scene linked to this pipeline
     * @param ratio The size of the postprocesses (0.5 means that your postprocess will have a width = canvas.width 0.5 and a height = canvas.height 0.5)
     * @param cameras The array of cameras that the rendering pipeline will be attached to
     */
    function LensRenderingPipeline(name, parameters, scene, ratio, cameras) {
        if (ratio === void 0) { ratio = 1.0; }
        var _this = _super.call(this, scene.getEngine(), name) || this;
        // Lens effects can be of the following:
        // - chromatic aberration (slight shift of RGB colors)
        // - blur on the edge of the lens
        // - lens distortion
        // - depth-of-field blur & highlights enhancing
        // - depth-of-field 'bokeh' effect (shapes appearing in blurred areas)
        // - grain effect (noise or custom texture)
        // Two additional texture samplers are needed:
        // - depth map (for depth-of-field)
        // - grain texture
        /**
         * @ignore
         * The chromatic aberration PostProcess id in the pipeline
         */
        _this.LensChromaticAberrationEffect = "LensChromaticAberrationEffect";
        /**
         * @ignore
         * The highlights enhancing PostProcess id in the pipeline
         */
        _this.HighlightsEnhancingEffect = "HighlightsEnhancingEffect";
        /**
         * @ignore
        * The depth-of-field PostProcess id in the pipeline
        */
        _this.LensDepthOfFieldEffect = "LensDepthOfFieldEffect";
        _this._pentagonBokehIsEnabled = false;
        _this._scene = scene;
        // Fetch texture samplers
        _this._depthTexture = scene.enableDepthRenderer().getDepthMap(); // Force depth renderer "on"
        if (parameters.grain_texture) {
            _this._grainTexture = parameters.grain_texture;
        }
        else {
            _this._createGrainTexture();
        }
        // save parameters
        _this._edgeBlur = parameters.edge_blur ? parameters.edge_blur : 0;
        _this._grainAmount = parameters.grain_amount ? parameters.grain_amount : 0;
        _this._chromaticAberration = parameters.chromatic_aberration ? parameters.chromatic_aberration : 0;
        _this._distortion = parameters.distortion ? parameters.distortion : 0;
        _this._highlightsGain = parameters.dof_gain !== undefined ? parameters.dof_gain : -1;
        _this._highlightsThreshold = parameters.dof_threshold ? parameters.dof_threshold : 1;
        _this._dofDistance = parameters.dof_focus_distance !== undefined ? parameters.dof_focus_distance : -1;
        _this._dofAperture = parameters.dof_aperture ? parameters.dof_aperture : 1;
        _this._dofDarken = parameters.dof_darken ? parameters.dof_darken : 0;
        _this._dofPentagon = parameters.dof_pentagon !== undefined ? parameters.dof_pentagon : true;
        _this._blurNoise = parameters.blur_noise !== undefined ? parameters.blur_noise : true;
        // Create effects
        _this._createChromaticAberrationPostProcess(ratio);
        _this._createHighlightsPostProcess(ratio);
        _this._createDepthOfFieldPostProcess(ratio / 4);
        // Set up pipeline
        _this.addEffect(new PostProcessRenderEffect(scene.getEngine(), _this.LensChromaticAberrationEffect, function () { return _this._chromaticAberrationPostProcess; }, true));
        _this.addEffect(new PostProcessRenderEffect(scene.getEngine(), _this.HighlightsEnhancingEffect, function () { return _this._highlightsPostProcess; }, true));
        _this.addEffect(new PostProcessRenderEffect(scene.getEngine(), _this.LensDepthOfFieldEffect, function () { return _this._depthOfFieldPostProcess; }, true));
        if (_this._highlightsGain === -1) {
            _this._disableEffect(_this.HighlightsEnhancingEffect, null);
        }
        // Finish
        scene.postProcessRenderPipelineManager.addPipeline(_this);
        if (cameras) {
            scene.postProcessRenderPipelineManager.attachCamerasToRenderPipeline(name, cameras);
        }
        return _this;
    }
    /**
     * Get the class name
     * @returns "LensRenderingPipeline"
     */
    LensRenderingPipeline.prototype.getClassName = function () {
        return "LensRenderingPipeline";
    };
    Object.defineProperty(LensRenderingPipeline.prototype, "scene", {
        // Properties
        /**
         * Gets associated scene
         */
        get: function () {
            return this._scene;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LensRenderingPipeline.prototype, "edgeBlur", {
        /**
         * Gets or sets the edge blur
         */
        get: function () {
            return this._edgeBlur;
        },
        set: function (value) {
            this.setEdgeBlur(value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LensRenderingPipeline.prototype, "grainAmount", {
        /**
         * Gets or sets the grain amount
         */
        get: function () {
            return this._grainAmount;
        },
        set: function (value) {
            this.setGrainAmount(value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LensRenderingPipeline.prototype, "chromaticAberration", {
        /**
         * Gets or sets the chromatic aberration amount
         */
        get: function () {
            return this._chromaticAberration;
        },
        set: function (value) {
            this.setChromaticAberration(value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LensRenderingPipeline.prototype, "dofAperture", {
        /**
         * Gets or sets the depth of field aperture
         */
        get: function () {
            return this._dofAperture;
        },
        set: function (value) {
            this.setAperture(value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LensRenderingPipeline.prototype, "edgeDistortion", {
        /**
         * Gets or sets the edge distortion
         */
        get: function () {
            return this._distortion;
        },
        set: function (value) {
            this.setEdgeDistortion(value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LensRenderingPipeline.prototype, "dofDistortion", {
        /**
         * Gets or sets the depth of field distortion
         */
        get: function () {
            return this._dofDistance;
        },
        set: function (value) {
            this.setFocusDistance(value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LensRenderingPipeline.prototype, "darkenOutOfFocus", {
        /**
         * Gets or sets the darken out of focus amount
         */
        get: function () {
            return this._dofDarken;
        },
        set: function (value) {
            this.setDarkenOutOfFocus(value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LensRenderingPipeline.prototype, "blurNoise", {
        /**
         * Gets or sets a boolean indicating if blur noise is enabled
         */
        get: function () {
            return this._blurNoise;
        },
        set: function (value) {
            this._blurNoise = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LensRenderingPipeline.prototype, "pentagonBokeh", {
        /**
         * Gets or sets a boolean indicating if pentagon bokeh is enabled
         */
        get: function () {
            return this._pentagonBokehIsEnabled;
        },
        set: function (value) {
            if (value) {
                this.enablePentagonBokeh();
            }
            else {
                this.disablePentagonBokeh();
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LensRenderingPipeline.prototype, "highlightsGain", {
        /**
         * Gets or sets the highlight grain amount
         */
        get: function () {
            return this._highlightsGain;
        },
        set: function (value) {
            this.setHighlightsGain(value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LensRenderingPipeline.prototype, "highlightsThreshold", {
        /**
         * Gets or sets the highlight threshold
         */
        get: function () {
            return this._highlightsThreshold;
        },
        set: function (value) {
            this.setHighlightsThreshold(value);
        },
        enumerable: false,
        configurable: true
    });
    // public methods (self explanatory)
    /**
     * Sets the amount of blur at the edges
     * @param amount blur amount
     */
    LensRenderingPipeline.prototype.setEdgeBlur = function (amount) { this._edgeBlur = amount; };
    /**
     * Sets edge blur to 0
     */
    LensRenderingPipeline.prototype.disableEdgeBlur = function () { this._edgeBlur = 0; };
    /**
     * Sets the amout of grain
     * @param amount Amount of grain
     */
    LensRenderingPipeline.prototype.setGrainAmount = function (amount) { this._grainAmount = amount; };
    /**
     * Set grain amount to 0
     */
    LensRenderingPipeline.prototype.disableGrain = function () { this._grainAmount = 0; };
    /**
     * Sets the chromatic aberration amount
     * @param amount amount of chromatic aberration
     */
    LensRenderingPipeline.prototype.setChromaticAberration = function (amount) { this._chromaticAberration = amount; };
    /**
     * Sets chromatic aberration amount to 0
     */
    LensRenderingPipeline.prototype.disableChromaticAberration = function () { this._chromaticAberration = 0; };
    /**
     * Sets the EdgeDistortion amount
     * @param amount amount of EdgeDistortion
     */
    LensRenderingPipeline.prototype.setEdgeDistortion = function (amount) { this._distortion = amount; };
    /**
     * Sets edge distortion to 0
     */
    LensRenderingPipeline.prototype.disableEdgeDistortion = function () { this._distortion = 0; };
    /**
     * Sets the FocusDistance amount
     * @param amount amount of FocusDistance
     */
    LensRenderingPipeline.prototype.setFocusDistance = function (amount) { this._dofDistance = amount; };
    /**
    * Disables depth of field
    */
    LensRenderingPipeline.prototype.disableDepthOfField = function () { this._dofDistance = -1; };
    /**
     * Sets the Aperture amount
     * @param amount amount of Aperture
     */
    LensRenderingPipeline.prototype.setAperture = function (amount) { this._dofAperture = amount; };
    /**
     * Sets the DarkenOutOfFocus amount
     * @param amount amount of DarkenOutOfFocus
     */
    LensRenderingPipeline.prototype.setDarkenOutOfFocus = function (amount) { this._dofDarken = amount; };
    /**
     * Creates a pentagon bokeh effect
     */
    LensRenderingPipeline.prototype.enablePentagonBokeh = function () {
        this._highlightsPostProcess.updateEffect("#define PENTAGON\n");
        this._pentagonBokehIsEnabled = true;
    };
    /**
     * Disables the pentagon bokeh effect
     */
    LensRenderingPipeline.prototype.disablePentagonBokeh = function () {
        this._pentagonBokehIsEnabled = false;
        this._highlightsPostProcess.updateEffect();
    };
    /**
     * Enables noise blur
     */
    LensRenderingPipeline.prototype.enableNoiseBlur = function () { this._blurNoise = true; };
    /**
     * Disables noise blur
     */
    LensRenderingPipeline.prototype.disableNoiseBlur = function () { this._blurNoise = false; };
    /**
     * Sets the HighlightsGain amount
     * @param amount amount of HighlightsGain
     */
    LensRenderingPipeline.prototype.setHighlightsGain = function (amount) {
        this._highlightsGain = amount;
    };
    /**
     * Sets the HighlightsThreshold amount
     * @param amount amount of HighlightsThreshold
     */
    LensRenderingPipeline.prototype.setHighlightsThreshold = function (amount) {
        if (this._highlightsGain === -1) {
            this._highlightsGain = 1.0;
        }
        this._highlightsThreshold = amount;
    };
    /**
     * Disables highlights
     */
    LensRenderingPipeline.prototype.disableHighlights = function () {
        this._highlightsGain = -1;
    };
    /**
     * Removes the internal pipeline assets and detaches the pipeline from the scene cameras
     * @param disableDepthRender If the scens depth rendering should be disabled (default: false)
     */
    LensRenderingPipeline.prototype.dispose = function (disableDepthRender) {
        if (disableDepthRender === void 0) { disableDepthRender = false; }
        this._scene.postProcessRenderPipelineManager.detachCamerasFromRenderPipeline(this._name, this._scene.cameras);
        this._chromaticAberrationPostProcess = null;
        this._highlightsPostProcess = null;
        this._depthOfFieldPostProcess = null;
        this._grainTexture.dispose();
        if (disableDepthRender) {
            this._scene.disableDepthRenderer();
        }
    };
    // colors shifting and distortion
    LensRenderingPipeline.prototype._createChromaticAberrationPostProcess = function (ratio) {
        var _this = this;
        this._chromaticAberrationPostProcess = new PostProcess("LensChromaticAberration", "chromaticAberration", ["chromatic_aberration", "screen_width", "screen_height", "direction", "radialIntensity", "centerPosition"], // uniforms
        [], // samplers
        ratio, null, Texture.TRILINEAR_SAMPLINGMODE, this._scene.getEngine(), false);
        this._chromaticAberrationPostProcess.onApply = function (effect) {
            effect.setFloat('chromatic_aberration', _this._chromaticAberration);
            effect.setFloat('screen_width', _this._scene.getEngine().getRenderWidth());
            effect.setFloat('screen_height', _this._scene.getEngine().getRenderHeight());
            effect.setFloat('radialIntensity', 1);
            effect.setFloat2('direction', 17, 17);
            effect.setFloat2('centerPosition', 0.5, 0.5);
        };
    };
    // highlights enhancing
    LensRenderingPipeline.prototype._createHighlightsPostProcess = function (ratio) {
        var _this = this;
        this._highlightsPostProcess = new PostProcess("LensHighlights", "lensHighlights", ["gain", "threshold", "screen_width", "screen_height"], // uniforms
        [], // samplers
        ratio, null, Texture.TRILINEAR_SAMPLINGMODE, this._scene.getEngine(), false, this._dofPentagon ? "#define PENTAGON\n" : "");
        this._highlightsPostProcess.onApply = function (effect) {
            effect.setFloat('gain', _this._highlightsGain);
            effect.setFloat('threshold', _this._highlightsThreshold);
            effect.setTextureFromPostProcess("textureSampler", _this._chromaticAberrationPostProcess);
            effect.setFloat('screen_width', _this._scene.getEngine().getRenderWidth());
            effect.setFloat('screen_height', _this._scene.getEngine().getRenderHeight());
        };
    };
    // colors shifting and distortion
    LensRenderingPipeline.prototype._createDepthOfFieldPostProcess = function (ratio) {
        var _this = this;
        this._depthOfFieldPostProcess = new PostProcess("LensDepthOfField", "depthOfField", [
            "grain_amount", "blur_noise", "screen_width", "screen_height", "distortion", "dof_enabled",
            "screen_distance", "aperture", "darken", "edge_blur", "highlights", "near", "far"
        ], ["depthSampler", "grainSampler", "highlightsSampler"], ratio, null, Texture.TRILINEAR_SAMPLINGMODE, this._scene.getEngine(), false);
        this._depthOfFieldPostProcess.onApply = function (effect) {
            effect.setTexture("depthSampler", _this._depthTexture);
            effect.setTexture("grainSampler", _this._grainTexture);
            effect.setTextureFromPostProcess("textureSampler", _this._highlightsPostProcess);
            effect.setTextureFromPostProcess("highlightsSampler", _this._depthOfFieldPostProcess);
            effect.setFloat('grain_amount', _this._grainAmount);
            effect.setBool('blur_noise', _this._blurNoise);
            effect.setFloat('screen_width', _this._scene.getEngine().getRenderWidth());
            effect.setFloat('screen_height', _this._scene.getEngine().getRenderHeight());
            effect.setFloat('distortion', _this._distortion);
            effect.setBool('dof_enabled', (_this._dofDistance !== -1));
            effect.setFloat('screen_distance', 1.0 / (0.1 - 1.0 / _this._dofDistance));
            effect.setFloat('aperture', _this._dofAperture);
            effect.setFloat('darken', _this._dofDarken);
            effect.setFloat('edge_blur', _this._edgeBlur);
            effect.setBool('highlights', (_this._highlightsGain !== -1));
            if (_this._scene.activeCamera) {
                effect.setFloat('near', _this._scene.activeCamera.minZ);
                effect.setFloat('far', _this._scene.activeCamera.maxZ);
            }
        };
    };
    // creates a black and white random noise texture, 512x512
    LensRenderingPipeline.prototype._createGrainTexture = function () {
        var size = 512;
        this._grainTexture = new DynamicTexture("LensNoiseTexture", size, this._scene, false, Texture.BILINEAR_SAMPLINGMODE);
        this._grainTexture.wrapU = Texture.WRAP_ADDRESSMODE;
        this._grainTexture.wrapV = Texture.WRAP_ADDRESSMODE;
        var context = this._grainTexture.getContext();
        var rand = function (min, max) {
            return Math.random() * (max - min) + min;
        };
        var value;
        for (var x = 0; x < size; x++) {
            for (var y = 0; y < size; y++) {
                value = Math.floor(rand(0.42, 0.58) * 255);
                context.fillStyle = 'rgb(' + value + ', ' + value + ', ' + value + ')';
                context.fillRect(x, y, 1, 1);
            }
        }
        this._grainTexture.update(false);
    };
    return LensRenderingPipeline;
}(PostProcessRenderPipeline));
export { LensRenderingPipeline };
//# sourceMappingURL=lensRenderingPipeline.js.map